<script setup>
import { computed, ref } from 'vue'
import { useBillingStore } from '@/stores/billing'
import { formatMoney } from '@/helpers/billing-format'

/**
 * Every invoice and everything that happened to the account's billing. Invoices
 * imported from Classic sit in the same list, read-only, with a Classic label.
 * Both tabs read from the billing store, one page at a time.
 */
const billingStore = useBillingStore()

const tab = ref('invoices')

const invoices = computed(() => billingStore.invoices)
const activity = computed(() => billingStore.activity)

const INVOICE_STATUSES = {
    paid: { label: 'Paid', class: 'bg-emerald-100 text-emerald-800' },
    finalized: { label: 'Due', class: 'bg-amber-100 text-amber-800' },
    disputed: { label: 'Disputed', class: 'bg-red-100 text-red-800' },
    uncollectible: {
        label: 'Written off',
        class: 'bg-taupe-200 text-taupe-700',
    },
    void: { label: 'Void', class: 'bg-taupe-200 text-taupe-700' },
    carried_forward: {
        label: 'Carried forward',
        class: 'bg-taupe-200 text-taupe-700',
    },
}

const statusOf = (invoice) =>
    INVOICE_STATUSES[invoice.status] ?? {
        label: invoice.status,
        class: 'bg-taupe-200 text-taupe-700',
    }

const isClassic = (invoice) => invoice.source === 'classic'

function actorLabel(actor) {
    switch (actor?.type) {
        case 'owner':
            return actor.name ?? 'Someone on your account'
        case 'admin':
            return 'Zenmanage support'
        default:
            return 'Zenmanage'
    }
}

// The facts an event carries, in the order people look for them.
function detailsOf(event) {
    const d = event.details ?? {}
    const parts = []

    if (d.plan_name) parts.push(d.plan_name)
    if (d.status) parts.push(d.status)
    if (d.invoice_number) parts.push(d.invoice_number)
    if (d.amount !== undefined && d.amount !== null) {
        parts.push(formatMoney(d.amount, d.currency))
    }

    return parts
}

function totalPages(page) {
    return page.pagination?.total_pages ?? 1
}

function changePage(which, page) {
    if (which === 'invoices') {
        return billingStore.refreshInvoices({ page })
    }
    return billingStore.refreshActivity({ page })
}
</script>

<template>
    <section
        data-testid="billing-history"
        aria-labelledby="billing-history-heading"
        class="rounded-xl border border-taupe-200 bg-white p-6 shadow-sm"
    >
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h2
                id="billing-history-heading"
                class="text-sm font-medium uppercase tracking-wide text-taupe-600"
            >
                History
            </h2>
            <zen-tab-list>
                <zen-tab
                    :active="tab === 'invoices'"
                    data-testid="history-tab-invoices"
                    @click="tab = 'invoices'"
                >
                    Invoices
                </zen-tab>
                <zen-tab
                    :active="tab === 'activity'"
                    data-testid="history-tab-activity"
                    @click="tab = 'activity'"
                >
                    Activity
                </zen-tab>
            </zen-tab-list>
        </div>

        <!-- Invoices -->
        <div
            v-if="tab === 'invoices'"
            class="mt-4"
            data-testid="history-invoices"
        >
            <div
                v-if="billingStore.loading.invoices && !invoices.items.length"
                class="space-y-3"
                data-testid="history-loading"
            >
                <zen-skeleton class="h-6 w-full" />
                <zen-skeleton class="h-6 w-full" />
                <zen-skeleton class="h-6 w-full" />
            </div>
            <div
                v-else-if="
                    billingStore.errors.invoices && !invoices.items.length
                "
                class="space-y-3"
                data-testid="history-error"
            >
                <p class="text-sm text-red-700">
                    We couldn't load your invoices.
                    {{ billingStore.errors.invoices }}
                </p>
                <zen-button
                    variant="secondary"
                    size="small"
                    @click="billingStore.refreshInvoices()"
                >
                    Try again
                </zen-button>
            </div>
            <p
                v-else-if="!invoices.items.length"
                class="text-sm text-taupe-600"
                data-testid="history-empty"
            >
                No invoices yet. Your first one shows up here when it's issued.
            </p>
            <div v-else class="overflow-x-auto">
                <table class="min-w-full text-sm">
                    <thead>
                        <tr class="text-left text-taupe-600">
                            <th class="py-2 pr-4 font-medium">Invoice</th>
                            <th class="px-4 py-2 font-medium">Date</th>
                            <th class="px-4 py-2 text-right font-medium">
                                Total
                            </th>
                            <th class="px-4 py-2 font-medium">Status</th>
                            <th class="py-2 pl-4 text-right font-medium">
                                <span class="sr-only">Download</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-taupe-200">
                        <tr
                            v-for="invoice in invoices.items"
                            :key="invoice.ulid"
                            :data-testid="`invoice-row-${invoice.number}`"
                        >
                            <td class="py-3 pr-4 font-medium text-taupe-900">
                                {{ invoice.number }}
                                <span
                                    v-if="isClassic(invoice)"
                                    class="ml-2 rounded-full bg-taupe-100 px-2 py-0.5 text-xs font-medium text-taupe-600"
                                    data-testid="invoice-classic-label"
                                    title="Issued before your account moved to the new billing system"
                                    >Classic</span
                                >
                            </td>
                            <td class="px-4 py-3 text-taupe-600">
                                <zen-date-time
                                    :value="invoice.created_at"
                                    format="date"
                                    utc
                                />
                            </td>
                            <td
                                class="px-4 py-3 text-right text-taupe-900"
                                data-testid="invoice-total"
                            >
                                {{
                                    formatMoney(invoice.total, invoice.currency)
                                }}
                            </td>
                            <td class="px-4 py-3">
                                <span
                                    class="rounded-full px-2.5 py-0.5 text-xs font-medium"
                                    :class="statusOf(invoice).class"
                                    data-testid="invoice-status"
                                    >{{ statusOf(invoice).label }}</span
                                >
                            </td>
                            <td class="py-3 pl-4 text-right">
                                <template v-if="invoice.receipts?.length">
                                    <zen-dropdown-menu placement="bottom-end">
                                        <template
                                            v-slot:trigger="{
                                                toggle,
                                                setReference,
                                                ariaAttrs,
                                            }"
                                        >
                                            <button
                                                :ref="setReference"
                                                type="button"
                                                v-bind="ariaAttrs"
                                                class="inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-sky-700 hover:bg-sky-100"
                                                :data-testid="`download-menu-${invoice.number}`"
                                                @click="toggle"
                                            >
                                                Download
                                                <zen-icon-chevron-down
                                                    class="h-4 w-4"
                                                />
                                            </button>
                                        </template>
                                        <template v-slot:default>
                                            <a
                                                v-if="invoice.invoice_url"
                                                :href="invoice.invoice_url"
                                                target="_blank"
                                                rel="noopener"
                                                role="menuitem"
                                                class="block px-4 py-2 text-sm text-taupe-700 hover:bg-taupe-100"
                                                >Invoice</a
                                            >
                                            <a
                                                v-for="receipt in invoice.receipts"
                                                :key="receipt.number"
                                                :href="receipt.url"
                                                target="_blank"
                                                rel="noopener"
                                                role="menuitem"
                                                class="block px-4 py-2 text-sm text-taupe-700 hover:bg-taupe-100"
                                                >Receipt {{ receipt.number }}</a
                                            >
                                        </template>
                                    </zen-dropdown-menu>
                                </template>
                                <a
                                    v-else-if="invoice.invoice_url"
                                    :href="invoice.invoice_url"
                                    target="_blank"
                                    rel="noopener"
                                    class="rounded-md px-2 py-1 text-sm font-medium text-sky-700 hover:bg-sky-100"
                                    :data-testid="`download-${invoice.number}`"
                                    >Download</a
                                >
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <zen-pagination
                v-if="invoices.pagination"
                :page="invoices.pagination.page"
                :total-pages="totalPages(invoices)"
                :total="invoices.pagination.total"
                @change="changePage('invoices', $event)"
            />
        </div>

        <!-- Activity -->
        <div v-else class="mt-4" data-testid="history-activity">
            <div
                v-if="billingStore.loading.activity && !activity.items.length"
                class="space-y-3"
                data-testid="history-loading"
            >
                <zen-skeleton class="h-6 w-full" />
                <zen-skeleton class="h-6 w-full" />
                <zen-skeleton class="h-6 w-full" />
            </div>
            <div
                v-else-if="
                    billingStore.errors.activity && !activity.items.length
                "
                class="space-y-3"
                data-testid="history-error"
            >
                <p class="text-sm text-red-700">
                    We couldn't load your activity.
                    {{ billingStore.errors.activity }}
                </p>
                <zen-button
                    variant="secondary"
                    size="small"
                    @click="billingStore.refreshActivity()"
                >
                    Try again
                </zen-button>
            </div>
            <p
                v-else-if="!activity.items.length"
                class="text-sm text-taupe-600"
                data-testid="history-empty"
            >
                Nothing has happened on your billing yet.
            </p>
            <ul v-else class="divide-y divide-taupe-200">
                <li
                    v-for="event in activity.items"
                    :key="event.ulid"
                    class="py-3"
                    :data-testid="`activity-${event.ulid}`"
                >
                    <div class="flex items-start justify-between gap-4">
                        <div class="min-w-0">
                            <div
                                class="text-sm font-medium text-taupe-900"
                                data-testid="activity-title"
                            >
                                {{ event.title }}
                            </div>
                            <div
                                v-if="
                                    detailsOf(event).length ||
                                    event.details?.effective_at
                                "
                                class="mt-0.5 text-sm text-taupe-600"
                                data-testid="activity-details"
                            >
                                {{ detailsOf(event).join(' · ') }}
                                <template v-if="event.details?.effective_at">
                                    <template v-if="detailsOf(event).length">
                                        · </template
                                    >Takes effect
                                    <zen-date-time
                                        :value="event.details.effective_at"
                                        format="date"
                                        utc
                                    />
                                </template>
                            </div>
                            <div
                                class="mt-0.5 text-xs text-taupe-500"
                                data-testid="activity-actor"
                            >
                                {{ actorLabel(event.actor) }}
                            </div>
                        </div>
                        <div class="shrink-0 text-sm text-taupe-600">
                            <zen-date-time
                                :value="event.occurred_at"
                                format="date"
                                utc
                            />
                        </div>
                    </div>
                </li>
            </ul>

            <zen-pagination
                v-if="activity.pagination"
                :page="activity.pagination.page"
                :total-pages="totalPages(activity)"
                :total="activity.pagination.total"
                @change="changePage('activity', $event)"
            />
        </div>
    </section>
</template>
